/**
 * «Должности и права» и права одного человека — §8.7 ТЗ платформы, D181.
 *
 * Реализация D68 по решениям владельца продукта 11.09.2026: роль — это
 * должность; галочки по разделам и действиям («видеть», «править»);
 * личные исключения поверх должности; должность «Собственник» со всеми
 * правами.
 *
 * Ни одна галочка здесь не защита: денежное право у должности, правка
 * «Собственника», чужой сервис — всё это сервер отклоняет сам (§8.4).
 * Серая галочка — подсказка, почему нажимать бесполезно, а не замок.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApiError,
  PeopleRightsScreen,
  PersonRights,
  PositionRights,
  UserServiceRights,
  RightObject,
  RightsScreen,
  UserRightsView,
  UserRow,
} from '../shell';
import { Button, Card, ErrorState, Field, Loading, Modal, Select, api, serviceTitle } from '../shell';

const ACTION_TITLE: Record<string, string> = { view: 'видеть', edit: 'править' };

/** Откуда у человека право — словами, а не кодом (§2 правил интерфейса). */
const SOURCE_TITLE: Record<string, string> = {
  host: 'владелец системы',
  owner: 'должность «Собственник»',
  admin: 'уровень «управляющий»',
  position: 'должность',
  default: 'стартовый набор',
  access: 'вход в финансы даёт все разделы',
  added: 'лично',
};

const RIGHTS_KEY = ['platform', 'rights'] as const;

function rightKey(object: string, action: string): string {
  return `${object}:${action}`;
}

function failure(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error && error.message
    ? `Не сохранилось: ${error.message}`
    : 'Не сохранилось. Попробуйте ещё раз.';
}

/**
 * Новая должность — с пустыми правами или слепком с другой.
 *
 * Слепок, а не связь: дальше должности живут отдельно, иначе правка прав
 * «Официанта» молча меняла бы «Старшего официанта». Денежных прав в копии
 * не бывает — должностям они не ставятся вовсе (§8.4), поэтому и
 * «Собственника» в списке нет: у него есть деньги.
 */
function NewPosition({ positions }: { positions: PositionRights[] }) {
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [copyFrom, setCopyFrom] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api.createPosition({
        name: name.trim(),
        copy_from: copyFrom ? Number(copyFrom) : null,
      }),
    onSuccess: () => {
      setName('');
      setCopyFrom('');
      client.invalidateQueries({ queryKey: RIGHTS_KEY });
      client.invalidateQueries({ queryKey: PEOPLE_KEY });
      client.invalidateQueries({ queryKey: ['platform', 'users'] });
    },
  });
  const problem = failure(create.error);

  return (
    <Card title="Новая должность">
      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        <Field label="Название">
          <input
            className="input"
            value={name}
            placeholder="Старший официант"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Права — как у">
          <Select value={copyFrom} onChange={(event) => setCopyFrom(event.target.value)}>
            <option value="">начать с пустых</option>
            {positions
              .filter((position) => !position.is_owner)
              .map((position) => (
                <option key={position.id} value={position.id}>
                  {position.name}
                </option>
              ))}
          </Select>
        </Field>
        <p className="faint">
          Права копируются один раз, слепком: дальше должности живут отдельно. Денежные права не
          копируются — они выдаются только поимённо.
        </p>
        <div className="row">
          <Button
            variant="primary"
            disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Создать
          </Button>
          {problem && <span className="faint">{problem}</span>}
          {create.isSuccess && !problem && (
            <span className="faint">
              Готово: должность заведена, права можно поправить в таблице ниже.
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}

export function PositionsAndRights() {
  const client = useQueryClient();
  const screen = useQuery<RightsScreen, ApiError>({
    queryKey: RIGHTS_KEY,
    queryFn: api.rightsScreen,
  });
  const change = useMutation({
    mutationFn: (input: {
      positionId: number;
      service: string;
      object: string;
      action: string;
      granted: boolean;
    }) =>
      api.setPositionRight(input.positionId, {
        service: input.service,
        object: input.object,
        action: input.action,
        granted: input.granted,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: RIGHTS_KEY }),
  });

  if (screen.isPending) return <Loading text="Загружаю права…" />;
  if (screen.error || !screen.data) return <ErrorState onRetry={() => screen.refetch()} />;

  const { services, objects, positions } = screen.data;
  const problem = failure(change.error);

  return (
    <>
      <Card title="Как устроены права">
        <ul className="stack" style={{ paddingLeft: 'var(--space-4)' }}>
          <li>
            Галочки ставятся <strong>должности</strong>: назначили человеку должность — он
            получил её права. Поверх можно добавить или убрать право лично — кнопка «Права»
            у человека на вкладке «Люди».
          </li>
          <li>
            «Видеть» — пользоваться разделом для себя. «Править» — управлять разделом: чужие
            записи, настройки.
          </li>
          <li>
            Денежное (💰) должности не ставится: одна галочка у «Повара» открыла бы долги всем
            поварам. Деньги выдаются только поимённо и только вами.
          </li>
          <li>
            «Собственник» получает всё, включая финансы, и вход во все три сервиса. Назначить
            эту должность можете только вы.
          </li>
          <li>
            Уровень «управляющий» в сервисе даёт все права этого сервиса, кроме денежных.
          </li>
          <li>
            «Стартовый набор» — права должности, которую ещё не настраивали. Первая же галочка
            делает их собственными правами должности.
          </li>
          <li>Вход в сервис по-прежнему выдаётся поимённо, на вкладке «Люди».</li>
        </ul>
      </Card>

      <NewPosition positions={positions} />

      {problem && <p className="faint">{problem}</p>}

      {services.map((service) => {
        const serviceObjects = objects.filter((item) => item.service === service);
        if (serviceObjects.length === 0) return null;
        return (
          <Card key={service} title={serviceTitle(service)}>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Должность</th>
                    {serviceObjects.map((item) => (
                      <th key={item.code} title={item.hint}>
                        {item.title}
                        {item.is_money && ' 💰'}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {positions.map((position) => (
                    <PositionRow
                      key={position.id}
                      position={position}
                      service={service}
                      objects={serviceObjects}
                      busy={change.isPending}
                      onToggle={(object, action, granted) =>
                        change.mutate({ positionId: position.id, service, object, action, granted })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}
    </>
  );
}

function PositionRow({
  position,
  service,
  objects,
  busy,
  onToggle,
}: {
  position: PositionRights;
  service: string;
  objects: RightObject[];
  busy: boolean;
  onToggle: (object: string, action: string, granted: boolean) => void;
}) {
  const rights = position.rights[service] ?? { configured: false, granted: [] };
  const granted = new Set(rights.granted);
  const owner = Boolean(position.is_owner);

  return (
    <tr>
      <td>
        <div className="table__name">{position.name}</div>
        {owner ? (
          <div className="table__sub">все права, включая деньги</div>
        ) : (
          !rights.configured && <div className="table__sub">стартовый набор</div>
        )}
      </td>
      {objects.map((item) => (
        <td key={item.code}>
          {item.is_money && !owner ? (
            <span className="faint" title="Денежное право выдаётся только поимённо — кнопкой «Права» у человека">
              только поимённо
            </span>
          ) : (
            <div className="stack" style={{ gap: 'var(--space-1)' }}>
              {item.actions.map((action) => (
                <label key={action} className="row" style={{ gap: 'var(--space-1)' }}>
                  <input
                    type="checkbox"
                    aria-label={`${position.name}: ${item.title} — ${ACTION_TITLE[action] ?? action}`}
                    checked={owner || granted.has(rightKey(item.code, action))}
                    disabled={busy || owner}
                    onChange={(event) => onToggle(item.code, action, event.target.checked)}
                  />
                  {ACTION_TITLE[action] ?? action}
                </label>
              ))}
            </div>
          )}
        </td>
      ))}
    </tr>
  );
}

const PEOPLE_KEY = ['platform', 'rights', 'people'] as const;

/** Право в клетке: есть ли оно, откуда и что сделает щелчок. */
function cellState(view: UserServiceRights | undefined, key: string) {
  const source = view?.granted[key];
  const removed = view?.removed.includes(key) ?? false;
  const checked = source !== undefined;
  // Снять личное исключение — значит вернуть «как у должности»; поставить
  // новое — «добавить» или «убрать» относительно того, что даёт должность.
  const next: boolean | null = checked
    ? source === 'added'
      ? null
      : false
    : removed
      ? null
      : true;
  return { source, removed, checked, next, personal: source === 'added' || removed };
}

/**
 * Вкладка «Права»: сотрудник × право, как панель прав в iiko (просьба
 * владельца 12.09.2026). Умолчания должностей — вторым переключателем:
 * они решают, что человек получает, едва ему назначили должность.
 */
export function RightsTab() {
  const [mode, setMode] = useState<'people' | 'positions'>('people');
  return (
    <>
      <div className="row" style={{ marginBottom: 'var(--space-3)', flexWrap: 'wrap' }}>
        <Button
          small
          variant={mode === 'people' ? 'primary' : 'quiet'}
          onClick={() => setMode('people')}
        >
          По сотрудникам
        </Button>
        <Button
          small
          variant={mode === 'positions' ? 'primary' : 'quiet'}
          onClick={() => setMode('positions')}
        >
          Умолчания должностей
        </Button>
      </div>
      {mode === 'people' ? <PeopleRights /> : <PositionsAndRights />}
    </>
  );
}

export function PeopleRights() {
  const client = useQueryClient();
  const screen = useQuery<PeopleRightsScreen, ApiError>({
    queryKey: PEOPLE_KEY,
    queryFn: api.peopleRights,
  });
  const change = useMutation({
    mutationFn: (input: {
      userId: number;
      service: string;
      object: string;
      action: string;
      granted: boolean | null;
    }) =>
      api.setUserRight(input.userId, {
        service: input.service,
        object: input.object,
        action: input.action,
        granted: input.granted,
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: PEOPLE_KEY });
      client.invalidateQueries({ queryKey: ['platform', 'user-rights'] });
    },
  });

  if (screen.isPending) return <Loading text="Загружаю права…" />;
  if (screen.error || !screen.data) return <ErrorState onRetry={() => screen.refetch()} />;

  const { services, objects, people } = screen.data;
  const problem = failure(change.error);

  return (
    <>
      <Card title="Права сотрудников">
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <p className="faint">
            Галочка — право у человека есть. Большинство прав приходит с должностью; щелчок
            меняет право только этому человеку, и такое право помечено «лично». Вернуть всё как
            у должности — кнопка «Права» у человека на вкладке «Люди».
          </p>
          <p className="faint">
            💰 — деньги: выдаются только поимённо и только вами. Приглушённые строки — у человека
            нет входа в этот сервис: права начнут действовать, когда вы его выдадите.
          </p>
        </div>
      </Card>

      {problem && <p className="faint">{problem}</p>}

      {services.map((service) => {
        const serviceObjects = objects.filter((item) => item.service === service);
        if (serviceObjects.length === 0) return null;
        return (
          <Card key={service} title={serviceTitle(service)}>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Сотрудник</th>
                    {serviceObjects.map((item) => (
                      <th key={item.code} title={item.hint}>
                        {item.title}
                        {item.is_money && ' 💰'}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {people.map((person) => (
                    <PersonRow
                      key={person.user_id}
                      person={person}
                      service={service}
                      objects={serviceObjects}
                      busy={change.isPending}
                      onToggle={(object, action, granted) =>
                        change.mutate({ userId: person.user_id, service, object, action, granted })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}
    </>
  );
}

function PersonRow({
  person,
  service,
  objects,
  busy,
  onToggle,
}: {
  person: PersonRights;
  service: string;
  objects: RightObject[];
  busy: boolean;
  onToggle: (object: string, action: string, granted: boolean | null) => void;
}) {
  const view = person.services[service];
  const noAccess = !person.is_host && !view?.access_level;
  const role = person.is_host
    ? 'владелец системы'
    : person.is_owner
      ? `${person.position_name ?? 'Собственник'} — все права`
      : person.position_name ?? 'должность не назначена';
  const name = person.full_name ?? 'аккаунт без сотрудника';

  return (
    <tr style={noAccess ? { opacity: 0.55 } : undefined}>
      <td>
        <div className="table__name">{name}</div>
        <div className="table__sub">
          {role}
          {noAccess && ' · нет входа'}
        </div>
      </td>
      {objects.map((item) => (
        <td key={item.code}>
          <div className="stack" style={{ gap: 'var(--space-1)' }}>
            {item.actions.map((action) => {
              const cell = cellState(view, rightKey(item.code, action));
              const note = cell.checked
                ? (SOURCE_TITLE[cell.source ?? ''] ?? cell.source)
                : cell.removed
                  ? 'лично снято'
                  : 'права нет';
              return (
                <label key={action} className="row" style={{ gap: 'var(--space-1)' }} title={note}>
                  <input
                    type="checkbox"
                    aria-label={`${name}: ${item.title} — ${ACTION_TITLE[action] ?? action}`}
                    checked={cell.checked}
                    disabled={busy || person.is_host}
                    onChange={() => onToggle(item.code, action, cell.next)}
                  />
                  {ACTION_TITLE[action] ?? action}
                  {cell.personal && <span className="badge badge--admin">лично</span>}
                </label>
              );
            })}
          </div>
        </td>
      ))}
    </tr>
  );
}

export function UserRightsDialog({
  user,
  isOwner,
  onClose,
}: {
  user: UserRow;
  isOwner: boolean;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const userKey = ['platform', 'user-rights', user.user_id] as const;
  const catalog = useQuery<RightsScreen, ApiError>({
    queryKey: RIGHTS_KEY,
    queryFn: api.rightsScreen,
  });
  const rights = useQuery<UserRightsView, ApiError>({
    queryKey: userKey,
    queryFn: () => api.userRights(user.user_id),
  });
  const refresh = () => client.invalidateQueries({ queryKey: userKey });
  const change = useMutation({
    mutationFn: (input: { service: string; object: string; action: string; granted: boolean | null }) =>
      api.setUserRight(user.user_id, input),
    onSuccess: refresh,
  });
  const reset = useMutation({
    mutationFn: (service: string) => api.resetUserRights(user.user_id, service),
    onSuccess: refresh,
  });

  const busy = change.isPending || reset.isPending;
  const problem = failure(change.error ?? reset.error);

  return (
    <Modal
      title={`Права: ${user.full_name ?? 'аккаунт без сотрудника'}`}
      onClose={onClose}
      actions={
        <Button variant="primary" onClick={onClose}>
          Готово
        </Button>
      }
    >
      {(catalog.isPending || rights.isPending) && <Loading text="Загружаю права…" />}
      {(catalog.error || rights.error) && (
        <ErrorState
          onRetry={() => {
            catalog.refetch();
            rights.refetch();
          }}
        />
      )}
      {catalog.data && rights.data && (
        <div className="stack">
          {user.is_host && (
            <p className="faint">Владелец системы видит и может всё — менять здесь нечего.</p>
          )}
          {isOwner && !user.is_host && (
            <p className="faint">
              Должность «Собственник»: все права во всех сервисах, включая финансы. Снять
              отдельное право можно — кнопкой ниже, лично.
            </p>
          )}
          {problem && <p className="faint">{problem}</p>}

          {catalog.data.services.map((service) => {
            const view = rights.data.services[service];
            const serviceObjects = catalog.data.objects.filter((item) => item.service === service);
            if (!view || serviceObjects.length === 0) return null;
            return (
              <section key={service} className="stack" style={{ gap: 'var(--space-2)' }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <strong>{serviceTitle(service)}</strong>
                  <Button
                    small
                    variant="quiet"
                    disabled={busy || user.is_host}
                    onClick={() => reset.mutate(service)}
                  >
                    Как у должности
                  </Button>
                </div>
                {!view.access_level && !user.is_host && (
                  <p className="faint">
                    Входа в этот сервис нет — права начнут действовать, когда вы его выдадите на
                    вкладке «Люди».
                  </p>
                )}
                {serviceObjects.map((item) => (
                  <div
                    key={item.code}
                    className="row"
                    style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}
                  >
                    <span title={item.hint}>
                      {item.title}
                      {item.is_money && ' 💰'}
                    </span>
                    <span className="row" style={{ gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                      {item.actions.map((action) => {
                        const key = rightKey(item.code, action);
                        const source = view.granted[key];
                        const removed = view.removed.includes(key);
                        const checked = source !== undefined;
                        // Снять личное исключение — значит вернуть «как у
                        // должности»; поставить новое — «добавить» или
                        // «убрать» относительно того, что даёт должность.
                        const next: boolean | null = checked
                          ? source === 'added'
                            ? null
                            : false
                          : removed
                            ? null
                            : true;
                        const note = checked
                          ? SOURCE_TITLE[source] ?? source
                          : removed
                            ? 'лично снято'
                            : '';
                        return (
                          <label key={action} className="row" style={{ gap: 'var(--space-1)' }}>
                            <input
                              type="checkbox"
                              aria-label={`${item.title} — ${ACTION_TITLE[action] ?? action}`}
                              checked={checked}
                              disabled={busy || user.is_host}
                              onChange={() =>
                                change.mutate({ service, object: item.code, action, granted: next })
                              }
                            />
                            {ACTION_TITLE[action] ?? action}
                            {note && <span className="faint">· {note}</span>}
                          </label>
                        );
                      })}
                    </span>
                  </div>
                ))}
              </section>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
